import {Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {FormBuilder, FormGroup, Validators, FormArray, FormControl, NgForm} from "@angular/forms";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {lastValueFrom} from "rxjs";
import {
    AiHintDto,
    DocumentType,
    KeywordSearchField,
    PerFormaAPIService,
    ScanDirectoryDto
} from "src/app/services/pfapi.service";
import {HttpErrorResponse} from "@angular/common/http";
import {AuthService} from "src/app/services/auth.service";
import {PerformaModulesService} from "src/app/services/performa-modules.service";

@Component({
    selector: "app-scan-directory-edit",
    standalone: false,
    templateUrl: "./scan-directory-edit.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./scan-directory-edit.component.scss"]
})
export class ScanDirectoryEditComponent implements OnInit {
    @ViewChild("aiHintModal") aiHintModal!: TemplateRef<any>;
    @ViewChild("promptModal") promptModal!: TemplateRef<any>;

    scanDirectory: ScanDirectoryDto = {
        ScanDirectoryPath: "",
        ScanSetupType: 0,
        AiPrompt: "",
        AiHints: []
    };
    documentTypes: DocumentType[] = [];
    metadataDefinitions: KeywordSearchField[] = [];
    typeKeywords: KeywordSearchField[] = [];
    isEditMode = false;
    loading = true;
    successMsg = "";
    errorMsg = "";
    currentHint: AiHintDto = {Id: undefined, Hint: ""};
    editingHintIndex: number | null = null;
    tempPrompt = "";
    newKeyword: AiHintDto = {};
    showsAiPrompt = false;
    showsKeywordHints = false;
    aiEnabled = false;

    // Alert state
    alertMessage = "";
    alertType: "success" | "error" | "warning" = "success";

    // Modal references
    private aiHintModalRef?: NgbModalRef;
    private promptModalRef?: NgbModalRef;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        authService: AuthService,
        public readonly modules: PerformaModulesService,
        private readonly api: PerFormaAPIService
    ) {
        this.showsKeywordHints = modules.features.includes("AiKeywordHint") || authService.isSedona;
        this.showsAiPrompt = modules.features.includes("AiPrompt") || authService.isSedona;
    }

    async ngOnInit() {
        this.route.queryParams.subscribe(p => {
            if (p.created === "true") {
                this.successMsg = "Created scan directory";
            }
        });
        // AI OCR options are only offered when AI is active for the instance
        this.aiEnabled = await this.modules.AiEnabled();
        const schema = await lastValueFrom(this.api.getApiScanDirectorySchema());
        this.documentTypes = schema.DocumentTypes;
        this.metadataDefinitions = schema.KeywordSearchFields ?? [];
        this.typeKeywords = schema.KeywordSearchFields ?? [];
        await this.checkEditMode();
    }

    docTypeChange = () => {
        if (Array.isArray(this.scanDirectory.AiHints)) {
            this.scanDirectory.AiHints = this.scanDirectory.AiHints.filter(x =>
                this.typeKeywords.some(y => y.ID === x.Id)
            );
        }
    };

    /* If they pick the AI setup type, set the document type to the first document 
        type choice. If it's null, it won't save. If they pick a non-AI setup type, then
        blank out the document type choice so they have to manually pick one and not 
        accidentally default to the first document type.
    */
    setupTypeChange = () => {
        this.scanDirectory.ScanDocumentType =
            this.scanDirectory.ScanSetupType == 5 ? this.documentTypes[0].ID : undefined;
    };

    removeKeyword = i => {
        this.scanDirectory.AiHints.splice(i, 1);
    };

    addKeyword = (frm: NgForm) => {
        frm.control.markAllAsTouched();
        if (!frm.valid) return;
        if (!Array.isArray(this.scanDirectory.AiHints)) this.scanDirectory.AiHints = [];
        this.scanDirectory.AiHints.push({...this.newKeyword});
        this.newKeyword = {};
        frm.control.markAsUntouched();
    };

    getKeywordName = id => {
        return this.typeKeywords?.find?.(x => x.ID === id)?.Name || "";
    };

    save = async (frm: NgForm) => {
        this.successMsg = "";
        this.errorMsg = "";
        frm.control.markAllAsTouched();
        if (!frm.valid) return;
        this.loading = true;
        if (this.scanDirectory.ScanSetupType == 5 && !this.scanDirectory.ScanDocumentType) {
            this.scanDirectory.ScanDocumentType = this.documentTypes?.[0]?.ID;
        }
        try {
            if (this.isEditMode) {
                await lastValueFrom(this.api.putApiScanDirectory(this.scanDirectory));
                frm.control.markAsUntouched();
                this.successMsg = "Modified scan directory";
            } else {
                const resp = await lastValueFrom(this.api.postApiScanDirectory(this.scanDirectory));
                this.router.navigateByUrl(`/configure/scandirectoryedit?id=${resp.ScanDirectoryID}&created=true`);
            }
            frm.control.markAsUntouched();
        } catch (e) {
            if (e.error?.ModelState?.status?.includes("FailScanDirectoryAlreadyExists")) {
                this.errorMsg = "A scan directory already exists with the name specified";
            }
        } finally {
            this.loading = false;
        }
    };

    remove = async () => {
        if (!confirm("Are you sure you want to delete this scan directory?")) {
            return;
        }
        try {
            this.loading = true;
            await lastValueFrom(this.api.deleteApiScanDirectoryId(this.scanDirectory.ScanDirectoryID));
            this.router.navigateByUrl("/configure/scandirectory");
        } finally {
            this.loading = false;
        }
    };

    checkEditMode = async () => {
        const id = this.route.snapshot.queryParamMap.get("id");
        if (!id) {
            this.loading = false;
            return;
        }
        this.isEditMode = true;
        try {
            this.scanDirectory = await lastValueFrom(this.api.getApiScanDirectoryId(Number(id)));
            console.log("Loaded scan directory:", this.scanDirectory);
        } finally {
            this.loading = false;
        }
    };
}
